import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import PageWrapper from "../../../components/layout/page/PageWrapper";
import PageHeader from "../../../components/layout/page/PageHeader";
import ContentPanel from "../../../components/layout/content/ContentPanel";

import Heading from "../../../components/typography/Heading";
import Text from "../../../components/typography/Text";

import BackButton from "../../../components/buttons/back/BackButton";
import SecondaryPillTabs from "../../../components/tabs/SecondaryPillTabs";
import EmptyState from "../../../components/feedback/EmptyState";

import ChannelVideosView from "../../videos/channels/views/ChannelVideosView";
import ChannelPopsView from "../../pops/channels/views/ChannelPopsView";
import ChannelAlbumsView from "../../albums/channels/views/ChannelAlbumsView";
import ChannelStreamsView from "../../streams/channels/views/ChannelStreamsView";

import ChannelFollowButton from "../components/ChannelFollowButton";
import { useChannelProfile } from "../hooks/useChannelProfile";

import "./ChannelPage.css";

const PREVIEW_LIMIT = 5;
const PAGE_LIMIT = 12;

const CHANNEL_TABS = [
  { id: "home", label: "Home" },
  { id: "videos", label: "Videos" },
  { id: "albums", label: "Albums" },
  {
    id: "more",
    label: "More",
    menu: [
      { id: "pops", label: "Pops" },
      { id: "streams", label: "Streams" },
    ],
  },
];

const VALID_TABS = ["home", "videos", "albums", "pops", "streams"];

const CHANNEL_VIEWS = {
  videos: ChannelVideosView,
  pops: ChannelPopsView,
  albums: ChannelAlbumsView,
  streams: ChannelStreamsView,
};

const PREVIEW_TYPES = ["videos", "pops", "albums", "streams"];

const getInitial = (name) =>
  String(name || "C")
    .trim()
    .charAt(0)
    .toUpperCase();

function ChannelMediaView({ type, username, preview = false }) {
  const Component = CHANNEL_VIEWS[type];

  if (!Component) return null;

  return (
    <Component
      username={username}
      preview={preview}
      limit={preview ? PREVIEW_LIMIT : PAGE_LIMIT}
    />
  );
}

export default function ChannelPage() {
  const navigate = useNavigate();
  const { creatorusername = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const username = String(creatorusername || "").trim();

  const {
    creator,
    loading: loadingProfile,
    error,
  } = useChannelProfile(username);

  const [avatarFailed, setAvatarFailed] = useState(false);

  const requestedTab = searchParams.get("tab");

  const activeTab = VALID_TABS.includes(requestedTab) ? requestedTab : "home";

  const brandName = String(
    creator?.brandName || creator?.brand_name || "",
  ).trim();

  const channelTitle = brandName || username || "Channel";

  const avatarUrl = creator?.avatarUrl || creator?.profileImageUrl || null;

  useEffect(() => {
    document.title = `${channelTitle} | Katch`;
  }, [channelTitle]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [username, avatarUrl]);

  const handleTabChange = (selected) => {
    const nextTab =
      typeof selected === "string"
        ? selected
        : selected?.id || selected?.value || selected?.path;

    if (!VALID_TABS.includes(nextTab)) return;
    if (nextTab === activeTab) return;

    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);

        if (nextTab === "home") {
          next.delete("tab");
        } else {
          next.set("tab", nextTab);
        }

        return next;
      },
      { preventScrollReset: true },
    );
  };

  const renderContent = () => {
    if (activeTab === "home") {
      return PREVIEW_TYPES.map((type) => (
        <ChannelMediaView
          key={`${username}-${type}`}
          type={type}
          username={username}
          preview
        />
      ));
    }

    return (
      <ChannelMediaView
        key={`${username}-${activeTab}`}
        type={activeTab}
        username={username}
      />
    );
  };

  if (!username || (error && !creator && !loadingProfile)) {
    return (
      <PageWrapper>
        <PageHeader>
          <BackButton onClick={() => navigate(-1)} />
        </PageHeader>

        <EmptyState
          title={!username ? "Creator unavailable" : "Channel unavailable"}
          description={
            !username
              ? "Creator username is required."
              : String(error?.message || error || "Unable to load channel.")
          }
          action={
            <Link to="/" className="katch-channel__back">
              Back to Katch
            </Link>
          }
        />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader marginTop={0}>
        <ContentPanel>
          <div className="katch-channel__profile">
            <div className="katch-channel__avatar">
              {avatarUrl && !avatarFailed ? (
                <img
                  src={avatarUrl}
                  alt={`${channelTitle} avatar`}
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                <span>{getInitial(brandName || username)}</span>
              )}
            </div>

            <div className="katch-channel__profile-info">
              <Heading as="h1" size="xl">
                {loadingProfile ? "Loading channel..." : channelTitle}
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                @{username}
              </Text>

              {!loadingProfile && creator && (
                <div className="katch-channel__actions">
                  <ChannelFollowButton username={username} />
                </div>
              )}
            </div>
          </div>
        </ContentPanel>
      </PageHeader>

      <div className="katch-channel__navigation">
        <SecondaryPillTabs
          tabs={CHANNEL_TABS}
          activeTab={activeTab}
          onChange={handleTabChange}
        />
      </div>

      <div className="katch-channel__content">{renderContent()}</div>
    </PageWrapper>
  );
}
